'use client';
import React, { useState, useEffect } from 'react';
import { Plus, Building2, ShieldCheck, Loader2, AlertTriangle } from 'lucide-react';
import { OWNER_LISTINGS } from './mockOwnerData';
import OwnerStatCards from './OwnerStatCards';
import ListingsTable from './ListingsTable';
import AddListingModal from './AddListingModal';
import OwnerChart from './OwnerChart';
import type { OwnerListing } from './mockOwnerData';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '../../../lib/supabase/client';
import { useRouter } from 'next/navigation';

export default function PropertyManagementClient() {
  const [listings, setListings] = useState<OwnerListing[]>(OWNER_LISTINGS);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const { user, loading } = useAuth();
  const router = useRouter();
  const supabase = createClient();
  const [verificationStatus, setVerificationStatus] = useState<string | null>(null);
  const [checkingVerification, setCheckingVerification] = useState(true);

  const totalViews = listings.reduce((s, l) => s + l.views, 0);
  const totalUnlocks = listings.reduce((s, l) => s + l.unlocks, 0);
  const activeCount = listings.filter(l => l.status === 'published' || l.status === 'featured').length;
  const revenueEstimate = totalUnlocks * 5;

  useEffect(() => {
    if (!loading && !user) {
      router.replace('/sign-up-login-screen');
      return;
    }
    if (!user) return;

    const checkVerification = async () => {
      setCheckingVerification(true);
      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        if (!profile || profile.role !== 'owner') {
          setVerificationStatus('not_owner');
          setCheckingVerification(false);
          return;
        }

        const { data: verification } = await supabase
          .from('seller_verifications')
          .select('overall_status')
          .eq('user_id', user.id)
          .maybeSingle();

        setVerificationStatus(verification?.overall_status || 'pending');
      } catch {
        // If tables don't exist yet, allow access (graceful fallback)
        setVerificationStatus('approved');
      } finally {
        setCheckingVerification(false);
      }
    };

    checkVerification();
  }, [user, loading]);

  if (loading || checkingVerification) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={32} className="animate-spin text-primary-500" />
      </div>
    );
  }

  // Not an owner
  if (verificationStatus === 'not_owner') {
    return (
      <div className="max-w-screen-lg mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={28} className="text-amber-500" />
        </div>
        <h2 className="text-xl font-700 text-gray-900 mb-2">Owner Account Required</h2>
        <p className="text-gray-500 text-sm mb-6">You need an owner account to manage property listings.</p>
        <button onClick={() => router.push('/sign-up-login-screen')}
          className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-600 rounded-xl transition-all duration-150">
          Sign Up as Owner
        </button>
      </div>
    );
  }

  // Verification pending or submitted
  if (verificationStatus === 'pending' || verificationStatus === 'submitted') {
    return (
      <div className="max-w-screen-lg mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldCheck size={28} className="text-primary-500" />
        </div>
        <h2 className="text-xl font-700 text-gray-900 mb-2">
          {verificationStatus === 'submitted' ? 'Verification Under Review' : 'Verification Required'}
        </h2>
        <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
          {verificationStatus === 'submitted' ?'Your documents are being reviewed. You\'ll be notified once approved (1–2 business days).'
            : 'You must complete seller verification before listing properties. It only takes a few minutes.'}
        </p>
        {verificationStatus === 'pending' && (
          <button onClick={() => router.push('/seller-verification')}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-600 rounded-xl transition-all duration-150 flex items-center gap-2 mx-auto">
            <ShieldCheck size={16} />
            Complete Verification
          </button>
        )}
      </div>
    );
  }

  // Verification rejected
  if (verificationStatus === 'rejected') {
    return (
      <div className="max-w-screen-lg mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={28} className="text-red-500" />
        </div>
        <h2 className="text-xl font-700 text-gray-900 mb-2">Verification Rejected</h2>
        <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
          Your verification was not approved. Please resubmit with valid documents or contact support.
        </p>
        <button onClick={() => router.push('/seller-verification')}
          className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-600 rounded-xl transition-all duration-150">
          Resubmit Verification
        </button>
      </div>
    );
  }

  // Approved — show full management UI
  return (
    <>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Building2 size={22} className="text-primary-500" />
              <h1 className="text-2xl font-700 text-gray-900">إدارة عقاراتي</h1>
            </div>
            <p className="text-sm text-gray-500">تتبع إعلاناتك، المشاهدات، وطلبات التواصل</p>
          </div>
          <button
            onClick={() => setAddModalOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-600 rounded-xl transition-all duration-150 active:scale-95 shadow-sm"
          >
            <Plus size={18} />
            إضافة إعلان جديد
          </button>
        </div>

        {/* Stat Cards */}
        <OwnerStatCards
          activeCount={activeCount}
          totalListings={listings.length}
          totalViews={totalViews}
          totalUnlocks={totalUnlocks}
          revenueEstimate={revenueEstimate}
        />

        {/* Chart */}
        <div className="mt-6">
          <OwnerChart />
        </div>

        {/* Table */}
        <div className="mt-6">
          <ListingsTable
            listings={listings}
            setListings={setListings}
          />
        </div>
      </div>

      {/* Add Modal */}
      {addModalOpen && (
        <AddListingModal
          onClose={() => setAddModalOpen(false)}
          onAdd={(newListing) => {
            setListings(prev => [newListing, ...prev]);
            setAddModalOpen(false);
          }}
        />
      )}
    </>
  );
}